import type { ReactNode } from "react";
import React, { createContext, useContext, useMemo, useCallback, useEffect, useState } from "react";
import type { Theme, Vars } from "./tokens";
import { theme, cssVar, vars } from "./tokens";

// =============================================================================
// TYPES
// =============================================================================

export type ThemeMode = "light" | "dark" | "system";

interface ThemeContextValue {
    /** Current theme object with var() references */
    theme: Theme;
    /** Current theme mode setting */
    themeMode: ThemeMode;
    /** Resolved theme (light or dark, after system preference applied) */
    resolvedTheme: "light" | "dark";
    /** Whether dark mode is currently active */
    isDarkMode: boolean;
    /** Set theme mode */
    setThemeMode: (mode: ThemeMode) => void;
    /** Toggle between light and dark */
    toggleTheme: () => void;
    /** Helper to create CSS var() references */
    cssVar: typeof cssVar;
    /** CSS variable name references */
    vars: Vars;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

// =============================================================================
// STORAGE KEY
// =============================================================================

const THEME_STORAGE_KEY = "ethos-theme-mode";

// =============================================================================
// SYSTEM PREFERENCE DETECTION
// =============================================================================

function getSystemPreference(): "light" | "dark" {
    if (typeof window === "undefined") return "light";
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

// =============================================================================
// THEME PROVIDER
// =============================================================================

interface ThemeProviderProps {
    children: ReactNode;
    /** Initial theme mode. Defaults to stored preference or 'light' */
    defaultMode?: ThemeMode;
    /** Storage key for persisting theme preference */
    storageKey?: string;
}

export function ThemeProvider({
    children,
    defaultMode,
    storageKey = THEME_STORAGE_KEY
}: ThemeProviderProps) {
    // Initialize theme mode from storage or default
    const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
        if (typeof window === "undefined") return defaultMode || "light";

        const stored = localStorage.getItem(storageKey);
        if (stored === "light" || stored === "dark" || stored === "system") {
            return stored;
        }
        return defaultMode || "light";
    });

    // Track system preference for "system" mode
    const [systemPreference, setSystemPreference] = useState<"light" | "dark">(getSystemPreference);

    // Resolve actual theme based on mode
    const resolvedTheme: "light" | "dark" = themeMode === "system" ? systemPreference : themeMode;
    const isDarkMode = resolvedTheme === "dark";

    // Listen for system preference changes
    useEffect(() => {
        const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

        const handleChange = (e: MediaQueryListEvent) => {
            setSystemPreference(e.matches ? "dark" : "light");
        };

        mediaQuery.addEventListener("change", handleChange);
        return () => mediaQuery.removeEventListener("change", handleChange);
    }, []);

    // Apply theme to document
    useEffect(() => {
        const root = document.documentElement;

        // Set data-theme attribute for CSS variable switching
        root.setAttribute("data-theme", resolvedTheme);

        // Also set class for convenience
        root.classList.remove("light", "dark");
        root.classList.add(resolvedTheme);

        // Update color-scheme for browser UI elements
        root.style.colorScheme = resolvedTheme;
    }, [resolvedTheme]);

    // Theme mode setter with persistence
    const setThemeMode = useCallback(
        (mode: ThemeMode) => {
            setThemeModeState(mode);
            localStorage.setItem(storageKey, mode);
        },
        [storageKey]
    );

    // Toggle between light and dark
    const toggleTheme = useCallback(() => {
        setThemeMode(isDarkMode ? "light" : "dark");
    }, [isDarkMode, setThemeMode]);

    // Memoize context value
    const value = useMemo<ThemeContextValue>(
        () => ({
            theme,
            themeMode,
            resolvedTheme,
            isDarkMode,
            setThemeMode,
            toggleTheme,
            cssVar,
            vars
        }),
        [themeMode, resolvedTheme, isDarkMode, setThemeMode, toggleTheme]
    );

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

// =============================================================================
// USE THEME HOOK
// =============================================================================

/**
 * Hook to access theme tokens and controls in React components.
 *
 * @example Basic usage
 * ```tsx
 * const { isDarkMode, toggleTheme } = useTheme();
 *
 * return (
 *   <button onClick={toggleTheme}>
 *     {isDarkMode ? '☀️' : '🌙'}
 *   </button>
 * );
 * ```
 *
 * @example Using CSS variable helpers
 * ```tsx
 * const { cssVar, vars } = useTheme();
 *
 * return (
 *   <div style={{
 *     color: cssVar(vars.colors.brand.primary),
 *     padding: cssVar(vars.spacing.md)
 *   }}>
 *     Hello World
 *   </div>
 * );
 * ```
 *
 * @example Using theme object
 * ```tsx
 * const { theme } = useTheme();
 *
 * return (
 *   <div style={{ color: theme.colors.brand.primary }}>
 *     Hello World
 *   </div>
 * );
 * ```
 */
export function useTheme(): ThemeContextValue {
    const context = useContext(ThemeContext);

    if (context === undefined) {
        // Return default values if used outside provider (for backwards compatibility)
        return {
            theme,
            themeMode: "light",
            resolvedTheme: "light",
            isDarkMode: false,
            setThemeMode: () => {
                console.warn("useTheme: setThemeMode called outside ThemeProvider");
            },
            toggleTheme: () => {
                console.warn("useTheme: toggleTheme called outside ThemeProvider");
            },
            cssVar,
            vars
        };
    }

    return context;
}

// =============================================================================
// STYLED UTILITIES
// =============================================================================

/**
 * Helper to create inline styles using theme tokens.
 *
 * @example
 * ```tsx
 * const styles = createStyles((t) => ({
 *   container: {
 *     backgroundColor: t.colors.background.primary,
 *     padding: t.spacing.lg,
 *   },
 * }));
 * ```
 */
export function createStyles<T extends Record<string, React.CSSProperties>>(stylesFn: (t: Theme) => T): T {
    return stylesFn(theme);
}

/**
 * Utility to conditionally apply dark mode styles.
 * Returns appropriate styles based on current theme.
 *
 * @example
 * ```tsx
 * const { isDarkMode } = useTheme();
 * const bgColor = themeValue(isDarkMode, '#1a1a1a', '#ffffff');
 * ```
 */
export function themeValue<T>(isDarkMode: boolean, darkValue: T, lightValue: T): T {
    return isDarkMode ? darkValue : lightValue;
}

// =============================================================================
// RE-EXPORTS
// =============================================================================

export { theme, cssVar, vars } from "./tokens";
export type { Theme, Vars } from "./tokens";
